import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Box,
  Divider,
  useToast,
  Icon,
} from '@chakra-ui/react';
import {
  User,
  Phone,
  Mail,
} from 'lucide-react';
import { TableCartItem, TableInfo, GuestInfo } from '../../types/tableOrdering';
import { useHotelCurrencyFromTable } from '../../hooks/useHotelCurrency';
import { getGuestInfoFromLatestOrder } from '../../utils/orderStorage';

interface GuestInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (guestInfo: GuestInfo) => void;
  tableInfo: TableInfo;
  cartItems: TableCartItem[];
  orderType: 'table_service' | 'pickup';
  total: number;
}

/**
 * GuestInfoModal Component
 * Captures guest information and payment method for table orders
 */
const GuestInfoModal: React.FC<GuestInfoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  tableInfo,
  cartItems,
  orderType,
  total
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    special_requests: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();

  // Returning guest on this device? Prefill name + phone from their last
  // order so they don't retype it every time.
  useEffect(() => {
    if (!isOpen) return;
    setFormData(prev => {
      if (prev.name || prev.phone) return prev;
      const previous = getGuestInfoFromLatestOrder();
      return previous ? { ...prev, name: previous.name, phone: previous.phone } : prev;
    });
  }, [isOpen]);

  // Use hotel currency hook for consistent formatting
  const { formatAmount: formatHotelAmount } = useHotelCurrencyFromTable(tableInfo);

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('tableOrdering.guestInfo.errors.nameRequired');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('tableOrdering.guestInfo.errors.phoneRequired');
    } else if (!/^[\d\s\-+()]{8,}$/.test(formData.phone.trim())) {
      newErrors.phone = t('tableOrdering.guestInfo.errors.phoneInvalid');
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = t('tableOrdering.guestInfo.errors.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      toast({
        title: t('tableOrdering.guestInfo.errors.correctErrors'),
        description: t('tableOrdering.guestInfo.errors.fillAll'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    // Prepare guest info
    const guestInfo: GuestInfo = {
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim() || undefined,
      special_requests: formData.special_requests.trim() || undefined,
    };

    try {
      onSubmit(guestInfo);
      
      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        special_requests: '',
      });
    } catch (error) {
      console.error('Error submitting guest info:', error);
      toast({
        title: t('tableOrdering.guestInfo.errors.errorTitle'),
        description: t('tableOrdering.guestInfo.errors.submitFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxW="500px" mx={4}>
        <ModalHeader pb={3}>
          <HStack spacing={3}>
            <Icon as={User} boxSize={6} color="blue.500" />
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold">
                {t('tableOrdering.guestInfo.title')}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {t('tableOrdering.guestInfo.subtitle')}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        
        <ModalCloseButton />
        
        <ModalBody pb={4}>
          <VStack spacing={4} align="stretch">
            
            {/* Order Summary */}
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <Box fontSize="sm">
                <Text fontWeight="semibold" mb={1}>
                  {t('tableOrdering.guestInfo.summaryHeader', { restaurant: tableInfo.restaurant.name, table: tableInfo.table_number })}
                </Text>
                <Text>
                  {t('tableOrdering.guestInfo.summaryItems', { count: cartItems.length, total: formatHotelAmount(total) })}
                </Text>
                <Text color="gray.600" mt={1}>
                  {orderType === 'table_service' ? t('tableOrdering.guestInfo.orderTypeTable') : t('tableOrdering.guestInfo.orderTypePickup')}
                </Text>
              </Box>
            </Alert>

            {/* Guest Information Form */}
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.name} isRequired>
                <FormLabel>
                  <HStack spacing={2}>
                    <Icon as={User} boxSize={4} />
                    <Text>{t('tableOrdering.guestInfo.fullName')}</Text>
                  </HStack>
                </FormLabel>
                <Input
                  placeholder={t('tableOrdering.guestInfo.fullNamePlaceholder')}
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.phone} isRequired>
                <FormLabel>
                  <HStack spacing={2}>
                    <Icon as={Phone} boxSize={4} />
                    <Text>{t('tableOrdering.guestInfo.phoneNumber')}</Text>
                  </HStack>
                </FormLabel>
                <Input
                  placeholder={t('tableOrdering.guestInfo.phoneNumberPlaceholder')}
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
                <FormErrorMessage>{errors.phone}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.email}>
                <FormLabel>
                  <HStack spacing={2}>
                    <Icon as={Mail} boxSize={4} />
                    <Text>{t('tableOrdering.guestInfo.emailOptional')}</Text>
                  </HStack>
                </FormLabel>
                <Input
                  placeholder={t('tableOrdering.guestInfo.emailPlaceholder')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>{t('tableOrdering.guestInfo.specialRequests')}</FormLabel>
                <Textarea
                  placeholder={t('tableOrdering.guestInfo.specialRequestsPlaceholder')}
                  value={formData.special_requests}
                  onChange={(e) => handleInputChange('special_requests', e.target.value)}
                  rows={3}
                />
              </FormControl>
            </VStack>

            <Divider />

            {/* Payment happens with the staff — no in-app card flow exists,
                so don't offer one. */}
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <Box fontSize="sm">
                <Text fontWeight="semibold">{t('tableOrdering.guestInfo.payInfoTitle')}</Text>
                <Text>
                  {t('tableOrdering.guestInfo.payInfoBody')}
                </Text>
              </Box>
            </Alert>
          </VStack>
        </ModalBody>
        
        <ModalFooter pt={4}>
          <HStack spacing={3} w="full">
            <Button variant="ghost" onClick={handleClose} flex={1}>
              {t('tableOrdering.guestInfo.cancel')}
            </Button>

            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText={t('tableOrdering.guestInfo.processing')}
              flex={2}
            >
              {t('tableOrdering.guestInfo.placeOrder')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GuestInfoModal;